// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// Third party imports
import { DxDataGridComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// Constant imports
import { MessageInterval } from '../../../../../_constants/messages-intervals';

// Service imports
import { PrecisionService } from '../../../../../_services/shared-services/precision.service';

// Model imports
import { ARCreditApplicationTransactionItemResultDto } from '../../../_models/account-receivable/credit-applications/ar-credit-application-transaction-item-result-dto';
import { ApplyAmountDataEntry } from '../../../_models/account-receivable/credit-applications/apply-amount';
import { ARCreditApplicationTargetTransactionResultDto } from '../../../_models/account-receivable/credit-applications/ar-credit-application-target-transaction-result-dto';

@Component({
    selector: 'app-ar-manual-line-item-credit-application',
    templateUrl: './ar-manual-line-item-credit-application.component.html',
    styleUrls: ['./ar-manual-line-item-credit-application.component.scss']
})
export class ARManualLineItemCreditApplicationComponent implements OnInit {
    @ViewChild('appliedAmountGrid', { static: false }) appliedAmountGrid: DxDataGridComponent;

    @Input() visible = false;
    @Input() sourceLineItemAppliedAmountsDataEntries: ApplyAmountDataEntry[] = [];
    @Input() selectedTargetItem: ARCreditApplicationTargetTransactionResultDto;
    @Input() remainingUnappliedAmount: number;
    @Input() totalAppliedAmount: number;

    @Output() onSave = new EventEmitter<any>();
    @Output() closeSpecifyApplyAmount = new EventEmitter<any>();

    saveIcon: string = IconConstants.SAVE;
    closeIcon: string = IconConstants.CLOSE;

    isSaved = false;
    sourceItems: ARCreditApplicationTransactionItemResultDto[] = [];
    targetItems: ARCreditApplicationTransactionItemResultDto[] = [];

    currencyFormat: any;
    totalApplyAmount = 0;

    totalApplyAmountData: any;
    remainingUnapplied: number;

    constructor(private readonly precisionService: PrecisionService) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
    }

    ngOnInit() {
        this.prepareSourceLineItems();
    }

    onHidden(e): void {
        this.closeApplyAmount();
    }

    onSaveClick(): void {
        this.isSaved = true;
        const emitParams = {
            totalTargetAppliedAmount: this.totalApplyAmount,
            editedSourceItems: this.sourceItems
        };
        this.onSave.emit(emitParams);
        this.closeApplyAmount();
    }

    onTargetRowSelect(event: any, target: ARCreditApplicationTransactionItemResultDto): void {
        if (Utils.isTrue(event.value)) {
            if (target.openBalance >= this.remainingUnapplied) {
                target.appliedAmount = this.remainingUnapplied;
                target.remainingOpenBalance = this.precisionService.getRoundedNumber(
                    target.openBalance - this.remainingUnapplied
                );
            } else {
                target.appliedAmount = target.openBalance;
                target.remainingOpenBalance = 0;
            }
        } else {
            this.remainingUnapplied = this.precisionService.getRoundedNumber(
                this.remainingUnapplied + target.appliedAmount
            );
            target.appliedAmount = 0;
            target.remainingOpenBalance = target.openBalance;
        }
        this.calculateTotalAppliedAmount();
    }

    targetApplyAmountChanged(event: any, target: ARCreditApplicationTransactionItemResultDto): void {
        if (Utils.isTrue(target.isSelect)) {
            if (event.value > this.remainingUnappliedAmount) {
                notify('The apply amount exceeds the unapply amount.', 'error', MessageInterval.errorMsgInterval);
                event.value = 0;
            } else if (event.value > target.openBalance) {
                notify(
                    'The apply amount exceeds the remaining open balance.',
                    'error',
                    MessageInterval.errorMsgInterval
                );
                event.value = 0;
            } else {
                if (target.openBalance >= this.remainingUnapplied) {
                    target.remainingOpenBalance = this.precisionService.getRoundedNumber(
                        target.openBalance - event.value
                    );
                } else {
                    if (event.value < target.openBalance) {
                        target.remainingOpenBalance = this.precisionService.getRoundedNumber(
                            target.openBalance - event.value
                        );
                    } else {
                        // otherwise event.value is equal, event.value > target.openBalance is handled with notify.
                        target.remainingOpenBalance = 0;
                    }
                }
                target.appliedAmount = event.value;
            }
            this.calculateTotalAppliedAmount();
        }
    }

    onRowSelect(event, target: ARCreditApplicationTransactionItemResultDto): void {
        if (Utils.isTrue(event.value)) {
            this.sourceLineItemAppliedAmountsDataEntries.forEach((line) => {
                if (Utils.equals(line.itemId, target.sourceItemId)) {
                    line.currentAppliedAmount = Math.min(line.remainingUnapplied, target.remainingOpenBalance);
                    line.appliedAmount = this.precisionService.getRoundedNumber(
                        line.appliedAmount + line.currentAppliedAmount
                    );
                    line.remainingUnapplied = this.precisionService.getRoundedNumber(
                        line.remainingUnapplied - line.currentAppliedAmount
                    );

                    target.appliedAmount = this.precisionService.getRoundedNumber(line.currentAppliedAmount);
                    target.currentAppliedAmount = this.precisionService.getRoundedNumber(line.currentAppliedAmount);
                    target.isSelect = true;
                }
            });
        } else {
            this.sourceLineItemAppliedAmountsDataEntries.forEach((line) => {
                if (Utils.equals(line.itemId, target.sourceItemId)) {
                    line.remainingUnapplied = this.precisionService.getRoundedNumber(
                        line.remainingUnapplied + target.appliedAmount
                    );
                    line.appliedAmount = this.precisionService.getRoundedNumber(
                        line.appliedAmount - target.appliedAmount
                    );
                    line.currentAppliedAmount = 0;

                    target.appliedAmount = 0;
                    target.currentAppliedAmount = 0;
                    target.isSelect = false;
                }
            });
        }

        this.updateRemainingOpenBalance(target);
        this.calculateTotalAppliedAmount();
    }

    applyAmountChanged(event, target): void {
        if (Utils.isTrue(target.isSelect) && Utils.notEquals(event.value, target.appliedAmount)) {
            this.sourceLineItemAppliedAmountsDataEntries.forEach((item) => {
                if (Utils.equals(item.itemId, target.sourceItemId)) {
                    item.targetLineItemCreditApps.forEach((targetItem) => {
                        if (Utils.equals(targetItem.itemId, target.itemId)) {
                            if (item.appliedAmount > 0) {
                                item.appliedAmount = this.precisionService.getRoundedNumber(
                                    item.appliedAmount - target.appliedAmount
                                );
                                item.remainingUnapplied = this.precisionService.getRoundedNumber(
                                    item.remainingUnapplied + target.appliedAmount
                                );
                            }
                            target.appliedAmount = 0;
                            target.isSelect = false;
                        }
                    });
                }
            });
        }

        if (event.value > 0) {
            // Note:- if already selected applied amount and updated manually
            this.updateRemainingOpenBalance(target);
            this.calculateTotalAppliedAmount();
            this.sourceLineItemAppliedAmountsDataEntries.forEach((item) => {
                if (Utils.equals(item.itemId, target.sourceItemId)) {
                    item.targetLineItemCreditApps.forEach((targetItem) => {
                        if (Utils.equals(targetItem.itemId, target.itemId)) {
                            if (event.value > item.remainingUnapplied) {
                                notify(
                                    'The apply amount exceeds the unapply amount.',
                                    'error',
                                    MessageInterval.errorMsgInterval
                                );
                                event.value = 0;
                                target.currentAppliedAmount = 0;
                            } else if (event.value > target.remainingOpenBalance) {
                                notify(
                                    'The apply amount exceeds the remaining open balance.',
                                    'error',
                                    MessageInterval.errorMsgInterval
                                );
                                event.value = 0;
                                target.currentAppliedAmount = 0;
                            } else {
                                item.appliedAmount = this.precisionService.getRoundedNumber(
                                    item.appliedAmount + event.value
                                );
                                item.currentAppliedAmount = event.value;
                                item.remainingUnapplied = this.precisionService.getRoundedNumber(
                                    item.remainingUnapplied - event.value
                                );
                                target.appliedAmount = event.value;
                                target.isSelect = true;
                            }
                        }
                    });
                }
            });
        }

        this.updateRemainingOpenBalance(target);
        this.calculateTotalAppliedAmount();
    }

    private calculateTotalAppliedAmount(): void {
        if (Utils.notNullOrEmpty(this.sourceItems)) {
            this.totalApplyAmount = this.precisionService.getRoundedNumber(
                this.sourceItems
                    .map((item) =>
                        item.targetItems.map((targetItem) => targetItem.appliedAmount).reduce((p, n) => p + n)
                    )
                    .reduce((p, n) => p + n)
            );
        } else {
            const totalApplyAmount = this.targetItems
                .map((item) => (Utils.notNullAndDefined(item.appliedAmount) ? item.appliedAmount : 0))
                .reduce((p, n) => p + n);

            this.totalApplyAmount = this.precisionService.getRoundedNumber(totalApplyAmount);

            if (Utils.equals(this.remainingUnappliedAmount, 0)) {
                this.remainingUnapplied = this.remainingUnappliedAmount;
                this.remainingUnappliedAmount = this.totalApplyAmount;
            } else {
                this.remainingUnapplied = this.precisionService.getRoundedNumber(
                    this.remainingUnappliedAmount - this.totalApplyAmount
                );
            }
        }
    }

    private prepareSourceLineItems(): void {
        const sourceItems = [];
        if (Utils.notNullOrEmpty(this.sourceLineItemAppliedAmountsDataEntries)) {
            this.sourceLineItemAppliedAmountsDataEntries.forEach((item) => {
                let sourceObj: ARCreditApplicationTransactionItemResultDto =
                    new ARCreditApplicationTransactionItemResultDto();
                sourceObj = Object.assign(sourceObj, item.sourceLineItemCreditApp);
                sourceObj.targetItems = [];
                item.targetLineItemCreditApps.forEach((targetItem) => {
                    sourceObj.targetItems.push({
                        ...targetItem,
                        sourceItemId: item.itemId,
                        remainingOpenBalance: targetItem.openBalance,
                        currentAppliedAmount: targetItem.appliedAmount
                    });
                });
                sourceItems.push(sourceObj);
            });
        } else {
            // When we don't have source line items. eg: Payment has no sourceLineItems
            this.targetItems = this.selectedTargetItem.lineItems;
            this.remainingUnappliedAmount = this.precisionService.getRoundedNumber(
                this.remainingUnappliedAmount + this.totalAppliedAmount
            );
            this.remainingUnapplied = this.remainingUnappliedAmount;

            if (this.totalAppliedAmount > 0) {
                this.targetItems.forEach((targetItem) => {
                    if (this.totalAppliedAmount > 0) {
                        if (Utils.isNullOrUndefined(targetItem.appliedAmount)) {
                            if (targetItem.openBalance >= this.totalAppliedAmount) {
                                targetItem.appliedAmount = this.totalAppliedAmount;
                                targetItem.remainingOpenBalance = this.precisionService.getRoundedNumber(
                                    targetItem.openBalance - targetItem.appliedAmount
                                );
                                this.totalAppliedAmount = 0;
                            } else {
                                targetItem.appliedAmount = targetItem.openBalance;
                                this.totalAppliedAmount = this.precisionService.getRoundedNumber(
                                    this.totalAppliedAmount - targetItem.appliedAmount
                                );
                                targetItem.remainingOpenBalance = 0;
                            }
                        } else {
                            this.totalAppliedAmount = this.precisionService.getRoundedNumber(
                                this.totalAppliedAmount - targetItem.appliedAmount
                            );
                        }
                        targetItem.isSelect = false;
                    }
                });
            }
            this.calculateTotalAppliedAmount();
        }
        this.sourceItems = sourceItems;
    }

    private updateRemainingOpenBalance(targetLineItem: ARCreditApplicationTransactionItemResultDto): void {
        const totalAppliedAmount = this.sourceItems
            .map((d) =>
                d.targetItems
                    .map((dt) => (Utils.equals(dt.itemId, targetLineItem.itemId) ? dt.appliedAmount : 0))
                    .reduce((p, n) => p + n)
            )
            .reduce((p, n) => p + n);

        this.sourceItems.forEach((sourceItem) => {
            sourceItem.targetItems.forEach((target) => {
                if (Utils.equals(targetLineItem.itemId, target.itemId)) {
                    target.remainingOpenBalance = this.precisionService.getRoundedNumber(
                        target.openBalance - totalAppliedAmount
                    );
                }
            });
        });
    }

    private resetAppliedAmount(): void {
        const sourceAppliedAmountList = this.sourceItems.map((sourceItem) => {
            if (Utils.notNullOrEmpty(sourceItem.targetItems)) {
                return this.precisionService.getRoundedNumber(
                    sourceItem.targetItems.map((d) => d.appliedAmount).reduce((p, n) => p + n)
                );
            } else {
                return 0;
            }
        });
        const sourceTotalAppliedAmount = Utils.notNullOrEmpty(sourceAppliedAmountList)
            ? this.precisionService.getRoundedNumber(sourceAppliedAmountList.reduce((p, n) => p + n))
            : 0;

        const appliedAmountList = this.sourceLineItemAppliedAmountsDataEntries.map((sourceItem) => {
            if (Utils.notNullOrEmpty(sourceItem.targetLineItemCreditApps)) {
                return this.precisionService.getRoundedNumber(
                    sourceItem.targetLineItemCreditApps.map((d) => d.appliedAmount).reduce((p, n) => p + n)
                );
            } else {
                return 0;
            }
        });
        const totalAppliedAmount = Utils.notNullOrEmpty(appliedAmountList)
            ? this.precisionService.getRoundedNumber(appliedAmountList.reduce((p, n) => p + n))
            : 0;

        if (Utils.notEquals(sourceTotalAppliedAmount, totalAppliedAmount)) {
            this.sourceItems.forEach((sourceItem) => {
                this.sourceLineItemAppliedAmountsDataEntries.forEach((item) => {
                    if (Utils.equals(sourceItem.itemId, item.itemId)) {
                        sourceItem.targetItems.forEach((targetItem) => {
                            item.appliedAmount = this.precisionService.getRoundedNumber(
                                item.appliedAmount - targetItem.appliedAmount
                            );
                            item.remainingUnapplied = this.precisionService.getRoundedNumber(
                                item.remainingUnapplied + targetItem.appliedAmount
                            );
                            targetItem.appliedAmount = 0;
                        });
                    }
                });
            });
        }
    }

    private closeApplyAmount(): void {
        this.visible = false;
        if (Utils.isFalse(this.isSaved)) {
            this.resetAppliedAmount();
        }
        this.closeSpecifyApplyAmount.emit(this.isSaved);
    }
}
